import React, { useState, useEffect } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, TextField, Button, Box, Typography, IconButton, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, Alert,
  FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, CardMedia, CardActions
} from '@mui/material';
import { Visibility as VisibilityIcon, Delete as DeleteIcon, Block as BlockIcon } from '@mui/icons-material';
import { momentsAPI } from '../utils/api';
import { CountryBadge } from '../components/SearchableCountrySelect';

const MomentsManagement = () => {
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMoment, setSelectedMoment] = useState(null);
  const [dialogAction, setDialogAction] = useState('');
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMoments();
    fetchCategories();
  }, []);

  const fetchMoments = async () => {
    try {
      const response = await momentsAPI.getMoments({ 
        search: searchTerm,
        category: categoryFilter 
      });
      setMoments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching moments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await momentsAPI.getCategories();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  const handleStatusChange = (moment, newStatus) => {
    setSelectedMoment(moment);
    setDialogAction(newStatus);
    setOpenDialog(true);
  };

  const handleDelete = async (momentId) => {
    try {
      await momentsAPI.deleteMoment(momentId);
      // Refresh the moments list
      fetchMoments();
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred');
    }
  };

  const confirmStatusChange = async () => {
    try {
      await momentsAPI.updateStatus(selectedMoment.id, { status: dialogAction });
      // Refresh the moments list
      fetchMoments();
      setOpenDialog(false);
      setSelectedMoment(null);
      setDialogAction('');
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMoment(null);
    setDialogAction('');
  };

  // Filter moments based on search term and category
  const filteredMoments = moments.filter(moment => {
    const matchesSearch = 
      moment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moment.user?.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moment.user?.last_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !categoryFilter || moment.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Moments Management
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search Moments"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearch}
          sx={{ minWidth: 300 }}
        />
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={handleCategoryChange}
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.slug}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button variant="outlined" onClick={fetchMoments}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="moments table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Thumbnail</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Likes</TableCell>
              <TableCell>Comments</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMoments.map((moment) => (
              <TableRow
                key={moment.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {moment.id}
                </TableCell>
                <TableCell>
                  {moment.image_url ? (
                    <img 
                      src={moment.image_url} 
                      alt={moment.title} 
                      style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} 
                    />
                  ) : 'N/A'}
                </TableCell>
                <TableCell>{moment.title}</TableCell>
                <TableCell>
                  {moment.user?.first_name || moment.user_name} {moment.user?.last_name || ''}
                </TableCell>
                <TableCell>
                  <CountryBadge country={moment.country || moment.user?.country} />
                </TableCell>
                <TableCell>{moment.category}</TableCell>
                <TableCell>
                  <Chip 
                    label={moment.status} 
                    color={
                      moment.status === 'active' ? 'success' : 
                      moment.status === 'inactive' ? 'default' : 
                      moment.status === 'pending' ? 'warning' : 'error'
                    } 
                    size="small" 
                  />
                </TableCell>
                <TableCell>{moment.likes_count || 0}</TableCell>
                <TableCell>{moment.comments_count || 0}</TableCell>
                <TableCell>{new Date(moment.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton 
                    color="primary" 
                    title="View Moment"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  
                  <IconButton 
                    color="secondary" 
                    onClick={() => handleStatusChange(moment, 'inactive')}
                    title="Deactivate"
                  >
                    <BlockIcon />
                  </IconButton>
                  
                  <IconButton 
                    color="success" 
                    onClick={() => handleStatusChange(moment, 'active')}
                    title="Activate"
                  >
                    <BlockIcon style={{ transform: 'scaleX(-1)' }} />
                  </IconButton>
                  
                  <IconButton 
                    color="error" 
                    onClick={() => handleDelete(moment.id)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {dialogAction} moment "{selectedMoment?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={confirmStatusChange} color={dialogAction === 'active' ? 'success' : 'secondary'}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MomentsManagement;